import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Trash2, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Document, Page, pdfjs } from 'react-pdf';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { SkeletonCard } from '@/components/ui/skeleton-enhanced';
import { CropManager } from './CropManager';
import { CropInterface } from './CropInterface';
import { CropAreaData } from './CropArea';
import { convertPdfToPng } from '@/utils/pdfConverter';
import { preprocessImage } from '@/utils/imagePreprocessing';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';


interface SimplifiedFilePreviewModalProps {
  filePath: string | null;
  fileType: 'pdf' | 'image';
  isOpen: boolean;
  onClose: () => void;
  onCropComplete?: (croppedBlob: Blob, pageIndex?: string) => void;
  onDelete?: () => void;
  onProcessCrops?: (crops: CropAreaData[], fullPageImages?: File[]) => void;
}

export const SimplifiedFilePreviewModal = ({
  isOpen,
  onClose,
  filePath,
  fileType,
  onCropComplete,
  onDelete,
  onProcessCrops
}: SimplifiedFilePreviewModalProps) => {
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  
  // PDF-specific state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  
  // Crop state
  const [crops, setCrops] = useState<CropAreaData[]>([]);
  const [showCropInterface, setShowCropInterface] = useState(false);
  const [currentPagePngUrl, setCurrentPagePngUrl] = useState<string>('');
  const [allPageImages, setAllPageImages] = useState<File[]>([]);
  const [processingCrops, setProcessingCrops] = useState(false);
  
  const { toast } = useToast();

  // PDF.js worker is now configured globally via pdfWorker.js import

  useEffect(() => {
    if (isOpen && filePath) {
      loadFileContent();
    }
  }, [isOpen, filePath]);

  const loadFileContent = async () => {
    if (!filePath) return;
    
    setLoading(true);
    setError(null);
    console.log('loadFileContent: Starting for', filePath, 'type:', fileType);
    
    try {
      // Extract filename from path
      const pathParts = filePath.split('/');
      const extractedFileName = pathParts[pathParts.length - 1];
      setFileName(extractedFileName);
      
      if (fileType === 'pdf') {
        // For PDFs, download the file and pass as File object to react-pdf
        console.log('loadFileContent: Processing PDF file with react-pdf');
        const { data: fileData, error: downloadError } = await supabase.storage
          .from('invoice-documents')
          .download(filePath);
        
        if (downloadError) {
          console.error('loadFileContent: Download error:', downloadError);
          throw downloadError;
        }
        
        if (!fileData) {
          throw new Error('No file data received');
        }
        
        // Validate MIME type
        if (fileData.type !== 'application/pdf') {
          console.warn('File MIME type is not PDF:', fileData.type);
        }
        
        // Create File object with proper metadata
        const pdfFile = new File([fileData], extractedFileName, { 
          type: 'application/pdf',
          lastModified: Date.now()
        });
        
        setPdfFile(pdfFile);
        console.log('loadFileContent: PDF File object created:', pdfFile.name, pdfFile.size, 'bytes');
        
        // Get public URL for download
        const { data } = await supabase.storage
          .from('invoice-documents')
          .getPublicUrl(filePath);
        setPreviewUrl(data.publicUrl);
      } else {
        // For images, get the file and create a blob URL
        console.log('loadFileContent: Processing image file');
        const { data: fileData, error: downloadError } = await supabase.storage
          .from('invoice-documents')
          .download(filePath);
        
        if (downloadError) {
          console.error('loadFileContent: Download error:', downloadError);
          throw downloadError;
        }
        
        if (!fileData) {
          throw new Error('No file data received');
        }
        
        const url = URL.createObjectURL(fileData);
        setPreviewUrl(url);
        console.log('loadFileContent: Image URL created:', url);
      }
      
    } catch (err) {
      console.error('loadFileContent: Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load file');
      toast({
        title: "Error",
        description: "Failed to load file for preview",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      console.log('loadFileContent: Completed');
    }
  };

  // PDF document load success handler
  const onDocumentLoadSuccess = async ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    setCurrentPage(1);
    setError(null); // Clear any previous errors
    console.log('PDF loaded successfully with', numPages, 'pages');
    
    // Convert PDF to PNG images for cropping
    if (pdfFile && fileType === 'pdf') {
      try {
        const convertedImages = await convertPdfToPng(pdfFile, { pages: 'all' });
        setAllPageImages(convertedImages);
        
        // Set current page PNG URL
        if (convertedImages.length > 0) {
          setCurrentPagePngUrl(URL.createObjectURL(convertedImages[0]));
        }
      } catch (error) {
        console.error('Failed to convert PDF to PNG for cropping:', error);
      }
    }
  };

  // PDF document load error handler
  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error details:', {
      message: error.message,
      name: error.name,
      stack: error.stack
    });
    setError(`Failed to load PDF: ${error.message}`);
    toast({
      title: "PDF Loading Error",
      description: `Unable to load PDF document: ${error.message}`,
      variant: "destructive"
    });
  };

  // Navigation handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      updateCurrentPagePngUrl(newPage);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      updateCurrentPagePngUrl(newPage);
    }
  };

  const updateCurrentPagePngUrl = (pageNum: number) => {
    if (allPageImages.length >= pageNum) {
      // Clean up previous URL
      if (currentPagePngUrl) {
        URL.revokeObjectURL(currentPagePngUrl);
      }
      setCurrentPagePngUrl(URL.createObjectURL(allPageImages[pageNum - 1]));
    }
  };

  // Scale handlers
  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      if (currentPagePngUrl) {
        URL.revokeObjectURL(currentPagePngUrl);
      }
      crops.forEach(crop => URL.revokeObjectURL(crop.previewUrl));
    };
  }, [previewUrl, currentPagePngUrl, crops]);

  const handleDownload = () => {
    if (previewUrl) {
      const a = document.createElement('a');
      a.href = previewUrl;
      a.download = fileName || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Crop handlers
  const handleStartCropping = () => {
    if (fileType === 'pdf' && currentPagePngUrl) {
      setShowCropInterface(true);
    } else if (fileType === 'image' && previewUrl) {
      setShowCropInterface(true);
    }
  };

  const handleCropComplete = (cropData: CropAreaData) => {
    setCrops(prev => [...prev, cropData]);
    setShowCropInterface(false);
    toast({
      title: "Crop saved ✅",
      description: `Added crop from page ${cropData.pageIndex + 1}`,
    });
  };

  const handleDeleteCrop = (cropId: string) => {
    setCrops(prev => {
      const cropToDelete = prev.find(c => c.id === cropId);
      if (cropToDelete) {
        URL.revokeObjectURL(cropToDelete.previewUrl);
      }
      return prev.filter(c => c.id !== cropId);
    });
  };

  const handleClearAllCrops = () => {
    crops.forEach(crop => URL.revokeObjectURL(crop.previewUrl));
    setCrops([]);
  };

  const handleProcessCrops = async () => {
    if (!onProcessCrops) return;
    
    setProcessingCrops(true);
    try {
      if (crops.length > 0) {
        // Process only cropped regions
        await onProcessCrops(crops);
      } else {
        // Process full pages
        let imagesToProcess: File[] = [];
        
        if (fileType === 'pdf' && allPageImages.length > 0) {
          imagesToProcess = allPageImages;
        } else if (fileType === 'image' && pdfFile) {
          imagesToProcess = [pdfFile];
        }
        
        await onProcessCrops([], imagesToProcess);
      }
      
      toast({
        title: "Processing Started",
        description: crops.length > 0 
          ? `Processing ${crops.length} cropped region(s)`
          : "Processing full document",
      });
      
      onClose();
    } catch (error) {
      toast({
        title: "Processing Failed",
        description: "Failed to start processing. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessingCrops(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              {fileName || 'File Preview'}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDownload} disabled={loading || !!error}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              {onProcessCrops && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleProcessCrops}
                  disabled={loading || processingCrops}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {processingCrops ? 'Processing...' : 'Process'}
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDelete}
                  className="text-destructive hover:text-destructive"
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="relative flex-1 bg-muted/10 overflow-hidden">
          {loading ? (
            <div className="h-[600px] p-6">
              {fileType === 'pdf' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-muted rounded animate-pulse" />
                      <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                      <div className="h-8 w-8 bg-muted rounded animate-pulse" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 bg-muted rounded animate-pulse" />
                      <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                      <div className="h-8 w-8 bg-muted rounded animate-pulse" />
                    </div>
                  </div>
                  <div className="flex items-center justify-center h-[500px]">
                    <div className="w-96 h-[500px] bg-muted rounded-lg animate-pulse shadow-lg" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="w-80 h-80 bg-muted rounded-lg animate-pulse" />
                </div>
              )}
              <LoadingSpinner 
                size="lg" 
                text={fileType === 'pdf' ? 'Preparing PDF document...' : 'Loading image...'} 
                className="absolute inset-0 bg-background/80 backdrop-blur-sm"
              />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <p className="text-destructive font-medium mb-2">Failed to load file</p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </div>
          ) : !previewUrl && fileType === 'image' ? (
            <div className="flex items-center justify-center h-96">
              <p className="text-muted-foreground">No preview available</p>
            </div>
          ) : fileType === 'pdf' ? (
            <div className="flex flex-col h-full">
              {/* PDF Controls */}
              <div className="flex items-center justify-between p-4 border-b bg-background/50">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPrevPage}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextPage}
                    disabled={currentPage >= totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={zoomOut}
                    disabled={scale <= 0.5}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground min-w-[4rem] text-center">
                    {Math.round(scale * 100)}%
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={zoomIn}
                    disabled={scale >= 3.0}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetZoom}
                  >
                    Reset
                  </Button>
                </div>
              </div>
              
              {/* PDF Viewer */}
              <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50">
                {pdfFile ? (
                  <Document
                    file={pdfFile}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <div className="flex items-center justify-center h-96">
                        <LoadingSpinner size="lg" text="Parsing PDF document..." />
                      </div>
                    }
                    error={
                      <div className="text-center p-4">
                        <p className="text-destructive mb-2">Failed to load PDF</p>
                        <p className="text-sm text-muted-foreground">
                          Check console for detailed error information
                        </p>
                      </div>
                    }
                    className="flex items-center justify-center"
                  >
                    <Page
                      pageNumber={currentPage}
                      scale={scale}
                      loading={
                        <div className="flex items-center justify-center w-96 h-[500px] bg-muted rounded-lg shadow-lg">
                          <LoadingSpinner size="default" text={`Rendering page ${currentPage}...`} />
                        </div>
                      }
                      error={
                        <div className="text-center p-4">
                          <p className="text-destructive">Failed to render page {currentPage}</p>
                        </div>
                      }
                      className="shadow-lg rounded-lg border"
                    />
                  </Document>
                ) : (
                  <div className="flex items-center justify-center h-96">
                    <LoadingSpinner size="lg" text="Preparing PDF file..." />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center p-4 animate-fade-in">
              <img
                src={previewUrl}
                alt="Preview"
                className="max-w-full max-h-96 object-contain rounded-lg shadow-lg transition-opacity duration-300"
                onLoad={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                style={{ opacity: '0' }}
              />
            </div>
          )}
        </div>

        {/* Crop Manager */}
        {onProcessCrops && !loading && !error && (
          <div className="mt-4 border-t pt-4">
            <CropManager
              crops={crops}
              onDeleteCrop={handleDeleteCrop}
              onClearAll={handleClearAllCrops}
              onStartCropping={handleStartCropping}
              isProcessing={processingCrops}
            />
          </div>
        )}
      </DialogContent>

      {/* Crop Interface Overlay */}
      {showCropInterface && (
        <CropInterface
          image={fileType === 'pdf' ? currentPagePngUrl : previewUrl}
          pageIndex={currentPage - 1}
          onCropComplete={handleCropComplete}
          onCancel={() => setShowCropInterface(false)}
        />
      )}
    </Dialog>
  );
};